import { useState } from "react";
import { AsyncCombobox } from "@/components/shared/async-combobox";
import { type CachedClient, useCachedClients } from "@/hooks/use-cached-clients";
import { useDebouncedValue } from "@/hooks/use-debounced-value";

interface TargetClientSelectProps {
  /** The cache is scoped per BU — the combobox is disabled without one. */
  targetBuId: string | null;
  value: string | null;
  onChange: (clientId: string, client: CachedClient) => void;
  selectedLabel?: string;
  disabled?: boolean;
  className?: string;
}

function clientLabel(client: CachedClient): string {
  const name =
    client.company || [client.surname, client.firstName].filter(Boolean).join(", ") || "Unknown";
  return `${name} (${client.clientId})`;
}

/**
 * Single-select for a target Coach Manager Client, backed by the local
 * `coach_manager_client` cache — not a live `ClientSearch` call (Coach
 * Manager's `ClientSearch` requires at least one non-empty search field, so
 * it can't populate a picker just from a BU being chosen; the cache, built
 * by the Client Search page, can). Mirrors `WidgetSelect`'s wrapper pattern
 * around the shared, presentational `AsyncCombobox`. See
 * `.claude/rules/frontend.md`.
 */
export function TargetClientSelect({
  targetBuId,
  value,
  onChange,
  selectedLabel,
  disabled,
  className,
}: TargetClientSelectProps) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);

  const { data, isFetching } = useCachedClients(targetBuId, debouncedSearch);
  const clients = data ?? [];
  const emptyText = "No clients cached for this BU yet — use Client Search to look one up first.";

  return (
    <AsyncCombobox
      value={value}
      onChange={onChange}
      options={clients}
      getOptionValue={(client) => client.clientId}
      getOptionText={clientLabel}
      getOptionLabel={(client) => <span>{clientLabel(client)}</span>}
      search={search}
      onSearchChange={setSearch}
      selectedLabel={selectedLabel}
      loading={isFetching}
      disabled={disabled || !targetBuId}
      className={className}
      placeholder={targetBuId ? "Select a client…" : "Select a target BU first"}
      searchPlaceholder="Filter by company, surname, or Client ID…"
      emptyText={emptyText}
    />
  );
}
